"use client";

import { Icon } from "@iconify-icon/react";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import { FadeInIntersection, SectionHeading } from "@/common/components";
import {
  Carousel,
  CarouselContent,
  CarouselDotButton,
  CarouselDotNavigation,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/common/components/carousel";
import { MigrationLink } from "@/common/components-2/migration-link";
import { SafeHydrate, useMobile } from "@/common/helpers";
import { useTranslations } from "@/services/i18n";

import {
  getTestimonialsCardClass,
  getTestimonialsCarouselItemClass,
  getTestimonialsWithPartnersSectionClass,
} from "./testimonials-with-partners-section.styles";
import type { TestimonialsWithPartnersSectionProps } from "./testimonials-with-partners-section.types";

export function TestimonialsWithPartnersSection({
  className,
  heading,
  headingPartners,
  items,
  partners,
  footerText,
  ...props
}: TestimonialsWithPartnersSectionProps) {
  const t = useTranslations("components.testimonialsWithPartnersSection");
  const isMobile = useMobile();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- To fix; add type to `autoplay`
  const autoplay = Autoplay({ delay: 4000, stopOnFocusIn: true, stopOnInteraction: true, stopOnMouseEnter: true });

  return (
    <section className={getTestimonialsWithPartnersSectionClass({ className })} {...props}>
      <FadeInIntersection threshold={0.2} delay={200} className="container md:max-w-[90%]">
        <SectionHeading size="lg">
          <h2>{heading}</h2>
        </SectionHeading>
        <Carousel
          className="mx-auto mt-12 mb-16 w-full px-3 lg:my-24 lg:max-w-[1400px]"
          plugins={[autoplay]}
          opts={{
            align: "start",
          }}
        >
          <CarouselContent className="items-center">
            {items.map(({ image, name, text, subtitle }, index) => (
              <CarouselItem
                key={`${name}-${image.title}`}
                className={getTestimonialsCarouselItemClass()}
                style={{ animationDelay: `${200 * index}ms` }}
              >
                <div className={getTestimonialsCardClass()}>
                  <div className="flex-1 font-semibold text-[13px] text-gray-600 italic leading-5 xl:text-lg 2xl:leading-8">
                    {text}
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm">{t("rating")}:</p>
                    <div className="mt-0.5 flex gap-2 pl-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Icon
                          // biome-ignore lint/suspicious/noArrayIndexKey: OK
                          key={i}
                          icon="bx:bxs-star"
                          className="text-secondary-500 text-xl"
                        />
                      ))}
                    </div>
                    <div className="mt-5 flex items-center gap-4 border-t pt-5">
                      <div>
                        <Image
                          src={image.url}
                          alt={image.title}
                          width={56}
                          height={56}
                          className="rounded-full max-2xl:max-h-10 max-2xl:max-w-10"
                        />
                      </div>
                      <div className="flex flex-col">
                        <p className="font-bold text-primary-500 text-sm xl:text-lg">{name}</p>
                        <p className="text-xs xl:text-sm">{subtitle}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="max-lg:hidden" />
          <CarouselNext className="max-lg:hidden" />
          <p className="mt-8 text-center text-[13px] tracking-tight max-lg:leading-4 lg:mt-10">{footerText}</p>
          <CarouselDotNavigation
            className="mt-6 lg:hidden"
            items={(selectedIndex) =>
              items.map((_, index) => (
                <CarouselDotButton
                  // biome-ignore lint/suspicious/noArrayIndexKey: OK
                  key={`testimonial-dot-${index}`}
                  index={index}
                  active={Boolean(index === selectedIndex)}
                  aria-label={`${index + 1}`}
                >
                  <span />
                </CarouselDotButton>
              ))
            }
          />
        </Carousel>
        <SectionHeading size="lg">
          <h2>{headingPartners}</h2>
        </SectionHeading>
        <SafeHydrate>
          {isMobile ? (
            <Carousel
              className="mt-3"
              opts={{
                align: "start",
              }}
            >
              <CarouselContent className="items-center">
                {partners.map(({ image, link }) => (
                  <CarouselItem key={link} className="basis-6/12">
                    <MigrationLink href={link} target="_blank" rel="noopener noreferrer">
                      <Image
                        src={image.url}
                        alt={image.title}
                        width={100}
                        height={100}
                        className="mx-auto w-full max-w-36 object-contain"
                      />
                    </MigrationLink>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselDotNavigation
                className="mt-6 lg:hidden"
                items={(selectedIndex) =>
                  partners.slice(0, -1).map((_, index) => (
                    <CarouselDotButton
                      // biome-ignore lint/suspicious/noArrayIndexKey: OK
                      key={`testimonial-dot-${index}`}
                      index={index}
                      active={Boolean(index === selectedIndex)}
                      aria-label={`${index + 1}`}
                    >
                      <span />
                    </CarouselDotButton>
                  ))
                }
              />
            </Carousel>
          ) : (
            <div className="lg:-mb-10 mt-10 flex items-center justify-between">
              {partners.slice(0, 5).map(({ image, link }) => (
                <MigrationLink
                  key={link}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Image
                    src={image.url}
                    alt={image.title}
                    width={200}
                    height={200}
                    className="mx-auto max-h-[55px] w-full max-w-52 object-contain"
                  />
                </MigrationLink>
              ))}
            </div>
          )}
        </SafeHydrate>
      </FadeInIntersection>
    </section>
  );
}
